import { useQuery } from '@tanstack/react-query';
import { ListAgenciesResponse, AgencyResource } from '@ab/ams-school-api/types/private/v1';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

export type AgencysSearchQuery = {
  'filter[q]'?: string;
  'filter[ids]'?: string[];
  'page[size]': number;
  'page[number]': number;
};

export type Agency = AgencyResource['attributes'] & { id: string };

const defaultValue: Agency[] = [];

export function useAgencies(query: AgencysSearchQuery, options?: { keepPreviousData?: boolean; enabled?: boolean }) {
  const request = useRequest();
  const { isLoading, isFetching, error, data, isPreviousData } = useQuery(
    ['agencies', query],
    async () => {
      const response = await request<ListAgenciesResponse>(
        `${config.apiHost}/private/v1/agencies?${convertToUrlSearchParams(query)}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );
      const data = response.data;
      const agencies = data.map((app) => {
        const attrs = app.attributes;
        return {
          ...attrs,
          id: app.id,
        };
      });

      return {
        // as we are using keepPreviousData, we need to know the result is based on which page
        pagination: {
          pageNumber: response.meta.pagination.pageNumber,
          pageSize: response.meta.pagination.pageSize,
        },
        agencies,
        totalAgencys: response.meta.pagination.total,
      };
    },
    {
      enabled: options?.enabled === undefined ? true : options.enabled,
      keepPreviousData: options?.keepPreviousData === undefined ? true : options?.keepPreviousData,
    }
  );

  return {
    isPreviousAgencysData: isPreviousData,
    agenciesPagination: data?.pagination,
    agencies: data?.agencies || defaultValue,
    agenciesError: error,
    isFetchingAgencys: isFetching,
    isLoadingAgencys: isLoading,
    totalAgencys: data?.agencies ?? 0,
  };
}

function isExpectedResponse(res: unknown): res is ListAgenciesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res) && Array.isArray(res.data));
}

const convertToUrlSearchParams = (params: AgencysSearchQuery) => {
  return new URLSearchParams({
    'page[size]': `${params['page[size]']}`,
    'page[number]': `${params['page[number]']}`,
  });
};
